import { Box, Divider } from '@mui/material'
import Paper from '@mui/material/Paper'

import Button from '@/app/component/atom/button'
import { CampaignTemplateType, ReminderMessage, Message as TMessage } from '@/app/module/campaigns/types'
import { SelectOption } from '@/app/types'

import { AddFromLibraryButton } from './add-from-library-button'
import { ContentTypeIcon } from './content-type-icon'
import { useMessageSnippets } from './hooks/use-message-snippets'
import { getEmptyMessage } from './utils/empty-message'

type MessageListProps = Pick<NoMessageProps, 'campaignType' | 'timezone' | 'onCreateMessage'> & {
  types: SelectOption<string>[]
}

const MessageList = ({ campaignType, types, timezone, onCreateMessage }: MessageListProps) => {
  return (
    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1rem 0' }}>
      {types.map((type) => (
        <Button
          disabled={type.disabled}
          key={type.value}
          sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          startIcon={<ContentTypeIcon type={type.value} />}
          tooltip={type.disabled ? type.disabledReason : undefined}
          variant="outlined"
          onClick={() => {
            if (type.disabled) {
              return
            }
            const message = getEmptyMessage(campaignType, type.value, timezone, 0)
            onCreateMessage(message)
          }}
        >
          {type.label}
        </Button>
      ))}
    </div>
  )
}

type NoMessageProps = {
  campaignType: CampaignTemplateType
  messageIds: string[]
  supportedMessageTypes: SelectOption<string>[]
  timezone: string
  onCreateMessage: (message: TMessage | ReminderMessage) => void
}

export function NoMessage({
  campaignType,
  messageIds,
  supportedMessageTypes,
  timezone,
  onCreateMessage,
}: NoMessageProps) {
  const { data: messageSnippets, isSupported } = useMessageSnippets({ supportedMessageTypes })

  if (campaignType === CampaignTemplateType.Drip) {
    const smsTypes = supportedMessageTypes.filter((type) => type.value.startsWith('sms'))
    const voiceTypes = supportedMessageTypes.filter((type) => type.value.startsWith('voice'))
    const whatsappTypes = supportedMessageTypes.filter((type) => type.value.startsWith('whatsapp'))
    const otherTypes = supportedMessageTypes.filter(
      (type) => !type.value.startsWith('sms') && !type.value.startsWith('voice') && !type.value.startsWith('whatsapp'),
    )

    return (
      <Paper id="no-message" sx={{ p: 2, mb: 2 }}>
        <p>Add your first part!</p>
        {isSupported && (
          <>
            <Divider />
            <Box py={2}>
              <AddFromLibraryButton
                campaignType={campaignType}
                index={0}
                messageSnippets={messageSnippets}
                messageIds={messageIds}
                published={false}
                timezone={timezone}
                total={0}
                onMessageCreate={onCreateMessage}
              />
            </Box>
            <Divider />
          </>
        )}
        <MessageList
          types={smsTypes}
          campaignType={campaignType}
          timezone={timezone}
          onCreateMessage={onCreateMessage}
        />
        <MessageList
          types={voiceTypes}
          campaignType={campaignType}
          timezone={timezone}
          onCreateMessage={onCreateMessage}
        />
        <MessageList
          types={whatsappTypes}
          campaignType={campaignType}
          timezone={timezone}
          onCreateMessage={onCreateMessage}
        />
        <MessageList
          types={otherTypes}
          campaignType={campaignType}
          timezone={timezone}
          onCreateMessage={onCreateMessage}
        />
      </Paper>
    )
  }

  return (
    <Paper id="no-message" sx={{ p: 2, mb: 2 }}>
      <p>Add your first part!</p>
      {isSupported && (
        <>
          <Divider />
          <Box py={2}>
            <AddFromLibraryButton
              campaignType={campaignType}
              index={0}
              messageSnippets={messageSnippets}
              messageIds={messageIds}
              published={false}
              timezone={timezone}
              total={0}
              onMessageCreate={onCreateMessage}
            />
          </Box>
          <Divider />
        </>
      )}
      <MessageList
        types={supportedMessageTypes}
        campaignType={campaignType}
        timezone={timezone}
        onCreateMessage={onCreateMessage}
      />
    </Paper>
  )
}
